import React from 'react';
import { Box, ButtonBase } from '@mui/material';
import { Check as CheckIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import type { ContextBalances, PairBalance } from '../../services/contextApi';
import { pendingNames } from '../../services/balances';
import { formatMoneyCents } from '../../theme/chart';
import { personColor, tokens } from '../../theme/tokens';
import { richText } from '../landing/richText';
import { BigNumber, Eyebrow, Panel, PersonDot } from './home/ui';

interface Props {
  balances: ContextBalances;
  currentUserId?: string;
  /** Opens the settle dialog for a pair the user is in: as the one who paid, or the one who got paid. */
  onSettle: (pair: PairBalance) => void;
}

/**
 * "Saldos": the user's standing in the group on top, then one line per
 * pair, already netted. The user's own pairs carry the action that closes
 * them: "Paguei" for a debt, "Recebi" for money owed to them. Pairs between
 * two other members are read-only.
 */
export const BalancesCard: React.FC<Props> = ({ balances, currentUserId, onSettle }) => {
  const { t, i18n } = useTranslation('dashboard');
  const money = (value: number) => formatMoneyCents(value, balances.currency, i18n.language);
  const indexOf = (userId: string) => Math.max(balances.members.findIndex((member) => member.userId === userId), 0);
  const nameOf = (userId: string) => balances.members.find((member) => member.userId === userId)?.firstName ?? '?';

  const mine = (pair: PairBalance) => pair.fromUserId === currentUserId || pair.toUserId === currentUserId;
  const receivable = balances.pairs.filter((pair) => pair.toUserId === currentUserId).reduce((sum, pair) => sum + pair.amount, 0);
  const payable = balances.pairs.filter((pair) => pair.fromUserId === currentUserId).reduce((sum, pair) => sum + pair.amount, 0);
  const net = Math.round((receivable - payable) * 100) / 100;

  // The user's own pairs first: those are the ones with something to do.
  const pairs = [...balances.pairs].sort((a, b) => Number(mine(b)) - Number(mine(a)) || b.amount - a.amount);
  const pending = pendingNames(balances, currentUserId);

  const headline =
    net > 0 ? { value: `+${money(net)}`, text: t('group.balances.toReceive'), color: tokens.accent }
    : net < 0 ? { value: `−${money(-net)}`, text: t('group.balances.toPay'), color: tokens.warn }
    : null;

  const pendingNote = pending.length > 0 && (
    <Box component='p' sx={{ m: 0, fontSize: 12.5, color: tokens.ink3 }}>
      {t(pending.length === 1 ? 'balances.pendingOne' : 'balances.pendingMany', { names: pending.join(', ') })}
    </Box>
  );

  // Nothing owed by anyone: one compact line, so the cards with numbers get the room.
  if (pairs.length === 0) {
    return (
      <Panel label={t('balances.title')} sx={{ flexDirection: 'row', alignItems: 'center', gap: 1.75, py: { xs: 2, md: 2.25 } }}>
        <Box
          component='span'
          aria-hidden
          sx={{ width: 40, height: 40, flexShrink: 0, borderRadius: '50%', bgcolor: tokens.accentSoft, color: tokens.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <CheckIcon sx={{ fontSize: 22 }} />
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25, minWidth: 0 }}>
          <Eyebrow>{t('balances.title')}</Eyebrow>
          <Box component='p' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 19, md: 21 }, fontWeight: 700, lineHeight: 1.25 }}>
            {t('balances.settled')}
          </Box>
          {pendingNote}
        </Box>
      </Panel>
    );
  }

  return (
    <Panel label={t('balances.title')} hero sx={{ gap: 2 }}>
      <Eyebrow>{t('balances.title')}</Eyebrow>
      {headline ? (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'baseline', columnGap: 1.5, rowGap: 0.75 }}>
          <BigNumber size={44} color={headline.color}>{headline.value}</BigNumber>
          <Box component='span' sx={{ color: tokens.ink2 }}>{headline.text}</Box>
        </Box>
      ) : (
        <Box component='p' sx={{ m: 0, fontFamily: tokens.display, fontSize: { xs: 22, md: 26 }, fontWeight: 700 }}>
          {t('group.balances.youAreEven')}
        </Box>
      )}

      <Box component='ul' sx={{ m: 0, p: 0, listStyle: 'none' }}>
        {pairs.map((pair) => {
          const iOwe = pair.fromUserId === currentUserId;
          const owedToMe = pair.toUserId === currentUserId;
          const other = iOwe ? pair.toUserId : pair.fromUserId;
          const person = personColor(indexOf(other));
          const amount = money(pair.amount);
          const text = iOwe
            ? t('group.balances.youOwe', { to: nameOf(pair.toUserId), amount })
            : owedToMe
              ? t('group.balances.owesYou', { from: nameOf(pair.fromUserId), amount })
              : t('group.balances.owes', { from: nameOf(pair.fromUserId), to: nameOf(pair.toUserId), amount });
          return (
            <Box
              component='li'
              key={`${pair.fromUserId}-${pair.toUserId}`}
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 1.5,
                py: 1.75,
                borderTop: `1px solid ${tokens.line}`,
                color: iOwe || owedToMe ? tokens.ink : tokens.ink2,
              }}
            >
              {iOwe || owedToMe ? (
                <PersonDot name={nameOf(other)} color={person.color} tint={person.tint} />
              ) : (
                <PersonDot name={nameOf(pair.fromUserId)} color={tokens.ink3} tint={tokens.bg} />
              )}
              <Box component='span' sx={{ flex: '1 1 180px', fontVariantNumeric: 'tabular-nums' }}>{richText(text)}</Box>
              {iOwe || owedToMe ? (
                <ButtonBase
                  onClick={() => onSettle(pair)}
                  sx={{
                    minHeight: 40,
                    px: 1.75,
                    gap: 0.75,
                    borderRadius: 99,
                    bgcolor: tokens.accentSoft,
                    color: tokens.accent,
                    font: 'inherit',
                    fontSize: 14,
                    fontWeight: 700,
                    '&:hover': { bgcolor: tokens.accentSoft2 },
                    '&.Mui-focusVisible': { outline: `2px solid ${tokens.accent}`, outlineOffset: 2 },
                  }}
                >
                  <CheckIcon aria-hidden sx={{ fontSize: 17 }} />
                  {iOwe ? t('group.balances.paid') : t('balances.received')}
                </ButtonBase>
              ) : (
                <Box component='span' sx={{ fontSize: 13, color: tokens.ink3 }}>{t('group.balances.betweenThem')}</Box>
              )}
            </Box>
          );
        })}
      </Box>

      {pendingNote}
    </Panel>
  );
};
